import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type IconName,
} from "@nexus/ui";
import type { PostFlightDoubleDownBlock } from "@/features/post-flight/api/post-flight.api";
import {
  DEFAULT_SCRIPT_COUNT,
  DOUBLE_DOWN_SCRIPT_COUNTS,
  adTitle,
  doubleDownPlan,
  isImageCreative,
  type DoubleDownExtra,
  type DoubleDownPlan,
  type DoubleDownPrimary,
  type PostFlightAd,
} from "@/features/post-flight/lib/post-flight-list";

const TOUCH_TARGET = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-0";

interface ScriptDraft {
  text: string;
  scripts: number;
}

function bestFitId(plan: DoubleDownPlan | null): string {
  if (!plan) return "";
  if (plan.primary) return "primary";
  return plan.extras.find((extra) => extra.isBestFit)?.id ?? "";
}

function selectionKey(plan: DoubleDownPlan | null): string {
  if (!plan) return "";
  return `${bestFitId(plan)}:${plan.extras.map((extra) => extra.id).join(",")}`;
}

function reconcileChosen(current: string[], plan: DoubleDownPlan | null): string[] {
  const allowed = new Set(plan?.extras.map((extra) => extra.id) ?? []);
  const best = bestFitId(plan);
  const kept = current.filter((id) => id !== "primary" && id !== best && allowed.has(id));
  return best ? [best, ...kept] : kept;
}

interface DoubleDownDialogProps {
  ad: PostFlightAd | null;
  /** When omitted, the dialog opens whenever `ad` is set. */
  open?: boolean;
  adUnavailable?: boolean;
  postFailure?: { kind: "stale-list" | "inline" | "image-blocked"; message: string } | null;
  isReloadingList?: boolean;
  onReloadList?: () => void;
  isGenerating?: boolean;
  canDoubleDown?: boolean;
  isTypeDisabled?: boolean;
  onGenerate?: (blocks: PostFlightDoubleDownBlock[]) => void;
  onClose: () => void;
}

export function DoubleDownDialog({
  ad,
  open,
  adUnavailable = false,
  postFailure = null,
  isReloadingList = false,
  onReloadList,
  isGenerating = false,
  canDoubleDown = true,
  isTypeDisabled = false,
  onGenerate,
  onClose,
}: DoubleDownDialogProps) {
  const { t } = useTranslation();
  const plan = ad ? doubleDownPlan(ad) : null;
  const [selected, setSelected] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ScriptDraft>>({});
  const [ownOn, setOwnOn] = useState(false);
  const [step, setStep] = useState<"pick" | "configure">("pick");
  const [focusId, setFocusId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<{ selected: string[]; drafts: Record<string, ScriptDraft>; ownOn: boolean } | null>(null);
  const lead = ad?.rows[0];
  const typeLabel = lead
    ? t(`postFlight.types.${lead.opportunity_type}`, lead.opportunity_type).toLowerCase()
    : "";
  const mediaRow = ad?.rows.find((row) => row.video_url);
  const mediaUrl = mediaRow?.video_url ?? null;
  const image = isImageCreative(mediaUrl, mediaRow?.object_type);
  const tiles = plan ? tilesFrom(plan, t, typeLabel) : [];
  const ownDraft = drafts.own ?? { text: "", scripts: DEFAULT_SCRIPT_COUNT };
  const ownText = ownDraft.text.trim();
  const ownSelected = ownOn && ownText.length > 0;
  const chosen = [
    ...selected.filter((id) => id !== "own"),
    ...(ownSelected ? ["own"] : []),
  ];
  const totalScripts = chosen.reduce((sum, id) => sum + (drafts[id]?.scripts ?? DEFAULT_SCRIPT_COUNT), 0);
  const focus = focusId === "own" ? ownTile(t) : tiles.find((tile) => tile.id === focusId) ?? null;
  const planSelectionKey = selectionKey(plan);
  const isOpen = open ?? ad !== null;
  const [submitLocked, setSubmitLocked] = useState(false);
  const generateBlocked =
    adUnavailable ||
    image ||
    isGenerating ||
    submitLocked ||
    isReloadingList ||
    postFailure?.kind === "stale-list" ||
    !onGenerate;

  useEffect(() => {
    if (!isGenerating) {
      setSubmitLocked(false);
    }
  }, [isGenerating]);

  useEffect(() => {
    const current = ad ? doubleDownPlan(ad) : null;
    setSelected(reconcileChosen([], current));
    setDrafts({});
    setOwnOn(false);
    setStep("pick");
    setFocusId(null);
    setSnapshot(null);
  }, [ad?.hash]);

  useEffect(() => {
    const splitAt = planSelectionKey.indexOf(":");
    const best = splitAt === -1 ? "" : planSelectionKey.slice(0, splitAt);
    const extraList = splitAt === -1 ? "" : planSelectionKey.slice(splitAt + 1);
    const allowed = new Set(extraList ? extraList.split(",") : []);
    setSelected((current) => {
      const kept = current.filter((id) => id !== "primary" && id !== best && allowed.has(id));
      return best ? [best, ...kept] : kept;
    });
  }, [planSelectionKey]);

  function draftFor(id: string): ScriptDraft {
    return drafts[id] ?? { text: "", scripts: DEFAULT_SCRIPT_COUNT };
  }

  function updateDraft(id: string, patch: Partial<ScriptDraft>) {
    setDrafts((current) => ({ ...current, [id]: { ...draftFor(id), ...current[id], ...patch } }));
  }

  function toggle(id: string) {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  }

  function openConfigure(id: string) {
    setSnapshot({ selected, drafts, ownOn });
    if (id !== "own" && !selected.includes(id)) setSelected((current) => [...current, id]);
    setFocusId(id);
    setStep("configure");
  }

  function acceptConfigure() {
    setSnapshot(null);
    setStep("pick");
  }

  function cancelConfigure() {
    if (snapshot) {
      setSelected(snapshot.selected);
      setDrafts(snapshot.drafts);
      setOwnOn(snapshot.ownOn);
    }
    setSnapshot(null);
    setStep("pick");
  }

  return (
    <Dialog open={isOpen} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent aria-describedby={undefined} variant="form" className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden sm:max-w-[720px]">
        {postFailure ? (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-[13px] text-amber-900 dark:text-amber-100"
          >
            <p>{postFailure.message}</p>
            {postFailure.kind === "stale-list" && onReloadList ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 border-amber-500/50 bg-transparent text-[13px] hover:bg-amber-500/10"
                disabled={isReloadingList}
                onClick={() => onReloadList()}
              >
                {isReloadingList
                  ? t("postFlight.doubleDown.error.reloadingList", "Reloading…")
                  : t("postFlight.doubleDown.error.reloadList", "Reload list")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {adUnavailable ? (
          <p role="alert" className="text-[13px] text-muted-foreground">
            {t("postFlight.doubleDown.error.adUnavailable", "This ad is no longer available.")}
          </p>
        ) : null}
        <div className="flex shrink-0 items-center gap-3">
          {mediaUrl && image ? (
            <img src={mediaUrl} alt="" className="h-12 w-9 shrink-0 rounded-sm object-cover" />
          ) : mediaUrl ? (
            <video src={mediaUrl} muted playsInline preload="metadata" className="h-12 w-9 shrink-0 rounded-sm object-cover" />
          ) : null}
          <div className="flex min-w-0 flex-col gap-0.5">
            <DialogTitle className="text-[15px] font-normal">{t("postFlight.doubleDown.title", "Double down")}</DialogTitle>
            {ad ? (
              <p className="truncate text-[12px] text-muted-foreground">
                {adTitle(ad)}
                {" · "}
                {step === "pick"
                  ? t("postFlight.doubleDown.pickOneOrMore", "Pick one or more")
                  : t("postFlight.doubleDown.detailsOptional", "Details · optional")}
              </p>
            ) : null}
          </div>
        </div>
        <div className="min-h-0 overflow-x-hidden overflow-y-auto">
        {adUnavailable || !ad ? null : step === "pick" ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-2">
            {tiles.map((tile) => {
              const on = selected.includes(tile.id);
              const draft = draftFor(tile.id);
              return (
                <Tile
                  key={tile.id}
                  tile={tile}
                  selected={on}
                  preview={on ? draft.text.trim() : ""}
                  scriptLabel={t("postFlight.doubleDown.scriptCount", { count: draft.scripts })}
                  bestFitLabel={t("postFlight.doubleDown.bestFit", "Best fit")}
                  configureLabel={t("postFlight.doubleDown.configure", "Configure")}
                  configureName={t("postFlight.doubleDown.configureTile", "Configure {{title}}", { title: tile.title })}
                  onToggle={() => toggle(tile.id)}
                  onConfigure={() => openConfigure(tile.id)}
                />
              );
            })}
            <div className={`col-span-full flex flex-col gap-2 rounded-md border p-3 ${ownSelected ? "border-primary bg-primary-subtle" : "border-border bg-card"}`}>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" aria-pressed={ownSelected} className={`flex min-w-0 flex-1 items-center gap-2 text-left ${TOUCH_TARGET}`} onClick={() => { if (ownText) setOwnOn((current) => !current); }}>
                  <Icon name="pencil" color="inherit" className="size-4" />
                  <span className="text-[13px]">{t("postFlight.doubleDown.writeYourOwn", "Write your own")}</span>
                </button>
                <ScriptCounts
                  label={t("postFlight.doubleDown.scripts", "Scripts")}
                  value={ownDraft.scripts}
                  onChange={(scripts) => updateDraft("own", { scripts })}
                />
                <span className={`flex size-4 items-center justify-center rounded-sm border ${ownSelected ? "border-primary bg-primary text-primary-foreground" : "border-input-border bg-card"}`}>
                  {ownSelected ? <Icon name="check" color="inherit" className="size-3" /> : null}
                </span>
              </div>
              <textarea
                aria-label={t("postFlight.doubleDown.instruction", "Instruction")}
                value={ownDraft.text}
                onChange={(event) => {
                  const text = event.target.value;
                  updateDraft("own", { text });
                  if (text.trim()) setOwnOn(true);
                }}
                placeholder={t("postFlight.doubleDown.ownPlaceholder", "e.g. Same script, filmed outdoors with a travel angle")}
                rows={2}
                className="w-full min-h-16 rounded-md border border-input-border bg-card px-2.5 py-2 text-[12px]"
              />
            </div>
          </div>
        ) : focus ? (
          <ConfigureStep
            tile={focus}
            draft={draftFor(focus.id)}
            onDraft={(patch) => updateDraft(focus.id, patch)}
          />
        ) : null}
        </div>
        <div className="-mx-5 -mb-5 flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-muted px-5 py-3">
          {step === "configure" ? (
            <Button type="button" variant="outline" onClick={cancelConfigure}>
              {t("common.cancel", "Cancel")}
            </Button>
          ) : (
            <span className="text-[12px] text-muted-foreground">
              {chosen.length === 0
                ? t("postFlight.doubleDown.pickAtLeastOne", "Pick at least one")
                : `${t("postFlight.doubleDown.summaryItems", { count: chosen.length })} · ${t("postFlight.doubleDown.summaryScripts", { count: totalScripts })}`}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            {step === "pick" ? (
              <GenerateButton
                canGenerate={canDoubleDown && !isTypeDisabled}
                disabled={!canDoubleDown || isTypeDisabled || chosen.length === 0 || generateBlocked}
                label={
                  chosen.length === 0
                    ? t("postFlight.doubleDown.generateIdle", "Generate")
                    : t("postFlight.doubleDown.generateCount", {
                        count: totalScripts,
                        defaultValue_one: "Generate {{count}} script",
                        defaultValue_other: "Generate {{count}} scripts",
                      })
                }
                deniedLabel={
                  canDoubleDown
                    ? t(
                        "postFlight.doubleDown.typeDisabled",
                        "Post-Flight is turned off for your organization. Ask a Nexus admin or customer manager to turn it on.",
                      )
                    : t(
                        "postFlight.doubleDown.noPermission",
                        "You don't have this permission for this organization. Ask a Nexus admin or customer manager.",
                      )
                }
                onGenerate={() => {
                  if (generateBlocked) return;
                  setSubmitLocked(true);
                  onGenerate?.(blocksFrom(chosen, tiles, drafts, t));
                }}
              />
            ) : (
              <Button type="button" onClick={acceptConfigure}>
                {t("postFlight.doubleDown.accept", "Accept")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GenerateButton({
  canGenerate,
  disabled,
  label,
  deniedLabel,
  onGenerate,
}: {
  canGenerate: boolean;
  disabled: boolean;
  label: string;
  deniedLabel: string;
  onGenerate: () => void;
}) {
  const button = (
    <Button type="button" variant="ai" disabled={disabled} iconStart="sparkles" onClick={onGenerate}>
      {label}
    </Button>
  );
  if (canGenerate) return button;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Disabled buttons fire no pointer events, so the span keeps the tooltip and cursor. */}
          <span tabIndex={0} className="inline-flex cursor-not-allowed [&>button]:pointer-events-none">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{deniedLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Tile({
  tile,
  selected,
  preview,
  scriptLabel,
  bestFitLabel,
  configureLabel,
  configureName,
  onToggle,
  onConfigure,
}: {
  tile: TileCopy;
  selected: boolean;
  preview: string;
  scriptLabel: string;
  bestFitLabel: string;
  configureLabel: string;
  configureName: string;
  onToggle: () => void;
  onConfigure: () => void;
}) {
  const bestFit = tile.isPrimary && !selected;
  return (
    <div
      className={`flex min-h-28 flex-col gap-2 rounded-md border p-3 ${
        selected ? "border-primary bg-primary-subtle" : bestFit ? "border-ai/60 bg-ai-fill-light" : "border-border bg-card"
      }`}
    >
      <button type="button" aria-pressed={selected} onClick={onToggle} className="flex flex-1 flex-col gap-2 text-left">
        <span className="flex items-center justify-between gap-1.5">
          <Icon name={iconFor(tile.title)} color="inherit" className="size-[18px]" />
          {selected ? (
            <span className="flex size-4 items-center justify-center rounded-sm bg-primary text-primary-foreground">
              <Icon name="check" color="inherit" className="size-3" />
            </span>
          ) : null}
          {bestFit ? (
            <span className="inline-flex h-[18px] items-center rounded-sm bg-[var(--ai-fill-chip)] px-1.5 text-[10px] text-[var(--ai-text)]">
              {bestFitLabel}
            </span>
          ) : null}
        </span>
        <span className="text-[13px] leading-snug">{tile.title}</span>
        <span className="text-[11px] leading-snug text-muted-foreground">{tile.detail}</span>
        {preview ? (
          <span className="flex items-start gap-1.5 rounded-sm border border-border bg-card px-2 py-1.5 text-[11px]">
            <Icon name="pencil" color="inherit" className="mt-0.5 size-3 shrink-0 text-primary" />
            <span className="line-clamp-2">{preview}</span>
          </span>
        ) : null}
      </button>
      <span className="mt-auto flex items-center gap-1.5">
        <span className="inline-flex h-5 items-center gap-1 rounded-sm bg-[var(--slate-chip-bg)] px-1.5 text-[10px] text-[var(--slate-chip-text)] tabular-nums">
          <Icon name="file-text" color="inherit" className="size-2.5" />
          {scriptLabel}
        </span>
        <button
          type="button"
          aria-label={configureName}
          onClick={onConfigure}
          className={`ml-auto inline-flex h-[26px] items-center gap-1 rounded-sm border border-border bg-card px-2 text-[11px] ${TOUCH_TARGET}`}
        >
          <Icon name="sliders-horizontal" color="inherit" className="size-3" />
          {configureLabel}
        </button>
      </span>
    </div>
  );
}

function ConfigureStep({
  tile,
  draft,
  onDraft,
}: {
  tile: TileCopy;
  draft: ScriptDraft;
  onDraft: (patch: Partial<ScriptDraft>) => void;
}) {
  const { t } = useTranslation();
  const kind = tile.context ?? contextKind(tile.title);
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name={iconFor(tile.title)} color="inherit" className="size-4 text-muted-foreground" />
        <span className="text-[13px]">{tile.title}</span>
        <div className="ml-auto">
          <ScriptCounts
            label={t("postFlight.doubleDown.scripts", "Scripts")}
            value={draft.scripts}
            onChange={(scripts) => onDraft({ scripts })}
          />
        </div>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px]">
          {t(`postFlight.doubleDown.context.${kind}.label`, contextLabel(kind))}
          {kind === "own" ? null : (
            <span className="text-muted-foreground"> {t("postFlight.doubleDown.optional", "· optional")}</span>
          )}
        </span>
        <textarea
          aria-label={t("postFlight.doubleDown.instruction", "Instruction")}
          value={draft.text}
          onChange={(event) => onDraft({ text: event.target.value })}
          placeholder={t(`postFlight.doubleDown.context.${kind}.placeholder`, contextPlaceholder(kind))}
          rows={3}
          className="w-full min-h-20 rounded-md border border-input-border bg-card px-2.5 py-2 text-[12px]"
        />
        <span className="text-[11px] text-muted-foreground">
          {t(`postFlight.doubleDown.context.${kind}.help`, contextHelp(kind))}
        </span>
      </label>
    </div>
  );
}

function ScriptCounts({ label, value, onChange }: { label: string; value: number; onChange: (count: number) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
      {label}
      <div className="inline-flex rounded-sm border border-border bg-card p-0.5">
        {DOUBLE_DOWN_SCRIPT_COUNTS.map((count) => (
          <button
            key={count}
            type="button"
            aria-label={t("postFlight.doubleDown.scriptCount", { count })}
            aria-pressed={value === count}
            className={`h-11 w-11 rounded-sm text-[12px] [@media(hover:hover)_and_(pointer:fine)]:h-[22px] [@media(hover:hover)_and_(pointer:fine)]:w-6 ${value === count ? "bg-primary-subtle text-primary" : "text-muted-foreground"}`}
            onClick={() => onChange(count)}
          >
            {count}
          </button>
        ))}
      </div>
    </div>
  );
}

type ContextKind = "hook" | "body" | "presenter" | "other" | "own" | "keep-hook" | "keep-body";

interface TileCopy {
  id: string;
  title: string;
  detail: string;
  isPrimary: boolean;
  targetKey: string;
  context?: ContextKind;
}

function tilesFrom(
  plan: DoubleDownPlan,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  typeLabel: string,
): TileCopy[] {
  const tiles: TileCopy[] = [];
  if (plan.primary) tiles.push({ ...primaryCopy(t, plan.primary), targetKey: plan.primary.read });
  for (const extra of plan.extras) tiles.push({ ...extraCopy(t, extra, typeLabel), targetKey: extra.id });
  return tiles;
}

function primaryCopy(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  primary: DoubleDownPrimary,
): Omit<TileCopy, "targetKey"> {
  const values = {
    hook: primary.hook,
    hookBase: primary.hookBase,
    hold: primary.hold,
    hookX: primary.hookX,
    holdX: primary.holdX,
  };
  const context = contextForRead(primary.read);
  if (primary.read === "both-even") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryBothEven", "Hook and hold at the average"),
      context,
      detail: t(
        "postFlight.doubleDown.primaryBothEvenDetail",
        "{{hook}} stop vs {{hookBase}} average, and {{hold}}.",
        values,
      ),
    };
  }
  if (primary.read === "hook-even") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryHookEven", "Hook at the average"),
      context,
      detail: t(
        "postFlight.doubleDown.primaryHookEvenDetail",
        "{{hook}} stop vs {{hookBase}} average ({{hookX}}).",
        values,
      ),
    };
  }
  if (primary.read === "hold-even") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryHoldEven", "Hold at the average"),
      context,
      detail: t("postFlight.doubleDown.primaryHoldEvenDetail", "{{hold}} ({{holdX}}).", values),
    };
  }
  if (primary.read === "hook-above" || primary.read === "hook-below") {
    const below = primary.read === "hook-below";
    return {
      id: "primary",
      isPrimary: true,
      title: t(
        below ? "postFlight.doubleDown.primaryHookBelow" : "postFlight.doubleDown.primaryHookAbove",
        below ? "Hook below average" : "Hook above average",
      ),
      context,
      detail: t(
        below ? "postFlight.doubleDown.primaryHookBelowDetail" : "postFlight.doubleDown.primaryHookAboveDetail",
        below
          ? "Only {{hook}} stop vs {{hookBase}} average ({{hookX}})."
          : "{{hook}} stop vs {{hookBase}} average ({{hookX}}).",
        values,
      ),
    };
  }
  if (primary.read === "hold-above" || primary.read === "hold-below") {
    const below = primary.read === "hold-below";
    return {
      id: "primary",
      isPrimary: true,
      title: t(
        below ? "postFlight.doubleDown.primaryHoldBelow" : "postFlight.doubleDown.primaryHoldAbove",
        below ? "Hold below average" : "Hold above average",
      ),
      context,
      detail: t(
        below ? "postFlight.doubleDown.primaryHoldBelowDetail" : "postFlight.doubleDown.primaryHoldAboveDetail",
        below ? "Only {{hold}} ({{holdX}})." : "{{hold}} ({{holdX}}).",
        values,
      ),
    };
  }
  if (primary.read === "new-hook") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryNewHook", "New hook, same body"),
      context,
      detail: t(
        "postFlight.doubleDown.primaryNewHookDetail",
        "Only {{hook}} stop vs {{hookBase}} average, but {{hold}}. The body keeps people; the opening doesn’t stop them.",
        values,
      ),
    };
  }
  if (primary.read === "new-body") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryNewBody", "Same hook, new body"),
      context,
      detail: t(
        "postFlight.doubleDown.primaryNewBodyDetail",
        "{{hook}} stop vs {{hookBase}} average, but only {{hold}}. People stop, then leave.",
        values,
      ),
    };
  }
  if (primary.read === "new-presenter") {
    return {
      id: "primary",
      isPrimary: true,
      title: t("postFlight.doubleDown.primaryNewPresenter", "Same script, new presenter"),
      context,
      detail: t(
        "postFlight.doubleDown.primaryNewPresenterDetail",
        "Hook and hold are both above average ({{hookX}}, {{holdX}}), so the script is doing the work. Change who delivers it.",
        values,
      ),
    };
  }
  return {
    id: "primary",
    isPrimary: true,
    title: t("postFlight.doubleDown.primaryShorterBody", "New hook, shorter body"),
    context,
    detail: t(
      "postFlight.doubleDown.primaryShorterBodyDetail",
      "Converts well, but hook and hold are both below average ({{hookX}}, {{holdX}}). Keep the offer, fix the delivery.",
      values,
    ),
  };
}

function extraCopy(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  extra: DoubleDownExtra,
  typeLabel: string,
): Omit<TileCopy, "targetKey"> {
  const title = t(`postFlight.doubleDown.levers.${extra.id}.title`, extra.title);
  const catalogDetail = t(`postFlight.doubleDown.levers.${extra.id}.detail`, extra.detail);
  const detail =
    extra.hookResult && extra.holdResult
      ? t(
          "postFlight.doubleDown.serviceScores",
          "Hook is {{hookResult}} ({{hookX}}). Hold is {{holdResult}} ({{holdX}}).",
          {
            hookResult: t(`postFlight.doubleDown.result.${extra.hookResult}`, extra.hookResult),
            holdResult: t(`postFlight.doubleDown.result.${extra.holdResult}`, extra.holdResult),
            hookX: extra.hookX ?? "—",
            holdX: extra.holdX ?? "—",
          },
        )
      : extra.effort
        ? t("postFlight.doubleDown.leverSuggestion", "{{copy}} Suggested for {{type}} · {{effort}}.", {
            copy: catalogDetail,
            type: typeLabel,
            effort: extra.effort.toLowerCase(),
          })
        : extra.detail
          ? catalogDetail
          : "";
  return {
    id: extra.id,
    isPrimary: extra.isBestFit === true,
    title,
    detail,
  };
}

function blocksFrom(
  chosen: readonly string[],
  tiles: readonly TileCopy[],
  drafts: Record<string, ScriptDraft>,
  t: (key: string, fallback: string) => string,
): PostFlightDoubleDownBlock[] {
  return chosen.map((id) => {
    const tile = id === "own" ? ownTile(t) : tiles.find((candidate) => candidate.id === id);
    const draft = drafts[id] ?? { text: "", scripts: DEFAULT_SCRIPT_COUNT };
    const title = tile?.title ?? t("postFlight.doubleDown.title", "Double down");
    const instruction = draft.text.trim() || tile?.detail.trim() || title;
    return {
      leverId: id === "primary" || id === "own" ? null : id,
      instruction,
      title,
      scriptCount: draft.scripts,
    };
  });
}

function ownTile(t: (key: string, fallback: string) => string): TileCopy {
  return {
    id: "own",
    isPrimary: false,
    targetKey: "",
    title: t("postFlight.doubleDown.writeYourOwn", "Write your own"),
    detail: "",
  };
}

function iconFor(title: string): IconName {
  const value = title.toLowerCase();
  if (value.includes("presenter") || value.includes("creator")) return "user";
  if (value.includes("new body") || value.includes("stitch")) return "copy";
  return "sparkles";
}

function contextForRead(read: DoubleDownPrimary["read"]): ContextKind {
  if (read === "hook-above") return "keep-hook";
  if (read === "hold-above") return "keep-body";
  if (read === "hook-even" || read === "hold-even" || read === "both-even") return "other";
  if (read === "new-body" || read === "hold-below") return "body";
  if (read === "new-presenter") return "presenter";
  return "hook";
}

function contextKind(title: string): ContextKind {
  const value = title.toLowerCase();
  if (value.includes("write your own")) return "own";
  if (value.includes("new hook")) return "hook";
  if (value.includes("new body") || value.includes("stitch")) return "body";
  if (value.includes("presenter") || value.includes("creator")) return "presenter";
  return "other";
}

function contextLabel(kind: ContextKind): string {
  if (kind === "hook") return "Add context for the new hooks";
  if (kind === "body") return "Add context for the new body";
  if (kind === "keep-hook") return "Add context for keeping this opening";
  if (kind === "keep-body") return "Add context for keeping the rest of the ad";
  if (kind === "presenter") return "Describe the presenter";
  if (kind === "own") return "What should the new versions do?";
  return "Add context";
}

function contextPlaceholder(kind: ContextKind): string {
  if (kind === "hook") return 'e.g. Use the hook: "Still packing like it’s 2010?" Open on the product, not the presenter.';
  if (kind === "body") return "e.g. Show the product in use within 5 seconds, end on the price";
  if (kind === "keep-hook") return "e.g. Keep the first line and open on the product";
  if (kind === "keep-body") return "e.g. Keep the demo and the offer after the opening";
  if (kind === "presenter") return "e.g. Woman in her 30s, casual, same setting";
  if (kind === "own") return "e.g. Same script, filmed outdoors with a travel angle";
  return "e.g. Keep the same music, change the setting to outdoors";
}

function contextHelp(kind: ContextKind): string {
  if (kind === "hook") return "A hook line to use, a tone, what to show first, or anything to avoid.";
  if (kind === "body") return "What should happen after the opening: the demo, the story, the offer or the length.";
  if (kind === "keep-hook") return "What to keep from the opening. Leave empty and Adge keeps the hook that already stops the scroll.";
  if (kind === "keep-body") return "What should stay after the opening. Leave empty and Adge keeps the part that already holds attention.";
  if (kind === "presenter") return "Who should present: age, gender, style, or a specific creator. Leave empty and Adge picks.";
  if (kind === "own") return "Describe the change in a sentence or two. Adge writes the scripts from it.";
  return "Anything the new versions should include, change or avoid.";
}
